import { useEffect, useState } from 'react'
import { useShellStore } from '@/store/shell-store'
import type { ClientOrgParams } from '@/types/focus-shell'

type Status = 'pending' | 'ok' | 'warn' | 'fail'

interface Check {
  id: string
  label: string
  detail: string
  status: Status
  ms?: number
  error?: string
}

interface EnvInfo {
  publicIp?: string
  ipLocation?: string
  clockSkewSec?: number
  clockStatus: Status
  serviceWorkerCount: number
  capabilities: {
    cookies: boolean
    localStorage: boolean
    webSocket: boolean
  }
  screen: string
  viewport: string
  timezone: string
  language: string
}

// --- URL resolution ---
// Cascade: orgParams.servidor_origen → import.meta.env → hardcoded fallback
// Mirrors the same cascade used by Focus-CNF and Focus-MonitorContribuciones.
const FALLBACK_API_URL = 'https://ws.summafx.com'
const FALLBACK_WS_URL = 'wss://ws.summafx.com'

// Verificaciones de carga de apps: apuntan a produccion por defecto (sin env definidas).
// En UAT se definen vacias (VITE_DIAG_*_URL='') para omitirlas y evitar que UAT toque produccion.
const diagEnv = import.meta.env as Record<string, string | undefined>
const MONITOR_BUNDLE_URL = diagEnv['VITE_DIAG_MONITOR_URL'] ?? 'https://monitor.summafx.com/assets/remoteEntry.js'
const CNF_BUNDLE_URL = diagEnv['VITE_DIAG_CNF_URL'] ?? 'https://cnf.summafx.com/assets/remoteEntry.js'

function resolveBackendUrls(orgParams: ClientOrgParams): { apiUrl: string; wsUrl: string } {
  const s = orgParams.servidor_origen
  const envApi = ((import.meta.env as Record<string, string>)['VITE_API_URL'] || undefined)
  const envWs = ((import.meta.env as Record<string, string>)['VITE_WS_URL'] || undefined)
  return {
    apiUrl: s?.monitor_api_url ?? envApi ?? FALLBACK_API_URL,
    wsUrl: s?.monitor_ws_url ?? envWs ?? FALLBACK_WS_URL,
  }
}

function buildChecks(apiUrl: string, wsUrl: string): Check[] {
  const checks: Check[] = [
    { id: 'focus', label: 'Focus Shell', detail: 'focus.summafx.com', status: 'pending' },
  ]
  if (MONITOR_BUNDLE_URL) checks.push({ id: 'monitor-bundle', label: 'Monitor de Contribuciones (carga de app)', detail: MONITOR_BUNDLE_URL, status: 'pending' })
  if (CNF_BUNDLE_URL) checks.push({ id: 'cnf-bundle', label: 'Confirmaciones CNF (carga de app)', detail: CNF_BUNDLE_URL, status: 'pending' })
  checks.push(
    { id: 'backend-https', label: 'Backend HTTPS', detail: apiUrl, status: 'pending' },
    { id: 'backend-wss', label: 'Backend WebSocket (tiempo real)', detail: wsUrl + '/ws', status: 'pending' },
    { id: 'firebase-auth', label: 'Firebase Auth (login)', detail: 'identitytoolkit.googleapis.com', status: 'pending' },
    { id: 'firestore', label: 'Firestore (configuración)', detail: 'firestore.googleapis.com', status: 'pending' },
  )
  return checks
}

async function probeHttp(url: string, timeoutMs = 8000): Promise<{ ok: boolean; ms: number; error?: string }> {
  const start = performance.now()
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    await fetch(url, { method: 'GET', mode: 'no-cors', signal: controller.signal, cache: 'no-store' })
    return { ok: true, ms: Math.round(performance.now() - start) }
  } catch (e: any) {
    return { ok: false, ms: Math.round(performance.now() - start), error: e?.message || String(e) }
  } finally {
    clearTimeout(timer)
  }
}

async function probeClockSkew(): Promise<{ skewSec: number; status: Status }> {
  try {
    const localBefore = Date.now()
    const res = await fetch(window.location.origin + '/?_t=' + localBefore, { method: 'HEAD', cache: 'no-store' })
    const localAfter = Date.now()
    const serverDateHeader = res.headers.get('date')
    if (!serverDateHeader) return { skewSec: 0, status: 'warn' }
    const serverTime = new Date(serverDateHeader).getTime()
    const localMid = (localBefore + localAfter) / 2
    const skewSec = Math.round((localMid - serverTime) / 1000)
    const abs = Math.abs(skewSec)
    const status: Status = abs < 30 ? 'ok' : abs < 300 ? 'warn' : 'fail'
    return { skewSec, status }
  } catch {
    return { skewSec: 0, status: 'warn' }
  }
}

async function probePublicIp(): Promise<{ ip?: string; location?: string }> {
  try {
    const res = await fetch('https://api.ipify.org?format=json', { cache: 'no-store' })
    const { ip } = await res.json()
    try {
      const geoRes = await fetch(`https://ipapi.co/${ip}/json/`, { cache: 'no-store' })
      const geo = await geoRes.json()
      const loc = [geo.city, geo.region, geo.country_name, geo.org].filter(Boolean).join(' · ')
      return { ip, location: loc || undefined }
    } catch {
      return { ip }
    }
  } catch {
    return {}
  }
}

async function probeServiceWorkers(): Promise<number> {
  if (!('serviceWorker' in navigator)) return 0
  try {
    const regs = await navigator.serviceWorker.getRegistrations()
    return regs.length
  } catch {
    return 0
  }
}

function probeCapabilities() {
  let localStorageOk = false
  try {
    localStorage.setItem('__diag', '1')
    localStorage.removeItem('__diag')
    localStorageOk = true
  } catch {}
  return {
    cookies: navigator.cookieEnabled,
    localStorage: localStorageOk,
    webSocket: typeof WebSocket !== 'undefined',
  }
}

async function clearCachesAndReload() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map(r => r.unregister()))
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k)))
    }
  } catch {}
  location.reload()
}

function probeWebSocket(url: string, timeoutMs = 5000): Promise<{ ok: boolean; ms: number; error?: string }> {
  return new Promise((resolve) => {
    const start = performance.now()
    let settled = false
    let ws: WebSocket
    const done = (ok: boolean, error?: string) => {
      if (settled) return
      settled = true
      try { ws?.close() } catch {}
      resolve({ ok, ms: Math.round(performance.now() - start), error })
    }
    try {
      ws = new WebSocket(url)
      ws.onopen = () => done(true)
      // Close before the open event can mean either: server rejected handshake (auth, etc.) OR firewall blocked.
      // The tell is timing: fast close = server responded (red OK), timeout = firewall silently dropped packets.
      ws.onclose = (e) => {
        const ms = performance.now() - start
        if (ms < 3000) {
          done(true, `servidor accesible (cierre rápido, code ${e.code})`)
        } else {
          done(false, `cierre tardío (code ${e.code}) — posible interferencia de proxy`)
        }
      }
      setTimeout(() => done(false, 'timeout — WebSocket bloqueado por el firewall (deep packet inspection)'), timeoutMs)
    } catch (e: any) {
      done(false, e?.message || String(e))
    }
  })
}

export default function DiagnosticoPage() {
  const orgParams = useShellStore(state => state.orgParams)
  const { apiUrl, wsUrl } = resolveBackendUrls(orgParams)

  const [checks, setChecks] = useState<Check[]>(() => buildChecks(apiUrl, wsUrl))
  const [running, setRunning] = useState(false)
  const [finishedAt, setFinishedAt] = useState<Date | null>(null)
  const [copied, setCopied] = useState(false)
  const [env, setEnv] = useState<EnvInfo | null>(null)

  const buildReport = () => {
    const lines: string[] = []
    lines.push('SUMMAFX · Focus — Diagnóstico de conectividad')
    lines.push(`Fecha: ${(finishedAt ?? new Date()).toLocaleString()}`)
    lines.push(`URL: ${window.location.href}`)
    lines.push(`Navegador: ${navigator.userAgent}`)
    if (env) {
      lines.push('')
      lines.push('— Entorno —')
      lines.push(`IP pública: ${env.publicIp ?? '(no obtenida)'}`)
      if (env.ipLocation) lines.push(`Ubicación: ${env.ipLocation}`)
      lines.push(`Reloj: ${env.clockSkewSec! >= 0 ? '+' : ''}${env.clockSkewSec}s vs servidor [${env.clockStatus.toUpperCase()}]`)
      lines.push(`Service Workers registrados: ${env.serviceWorkerCount}`)
      lines.push(`Cookies: ${env.capabilities.cookies ? 'ok' : 'DESHABILITADAS'} · localStorage: ${env.capabilities.localStorage ? 'ok' : 'DESHABILITADO'} · WebSocket API: ${env.capabilities.webSocket ? 'ok' : 'NO DISPONIBLE'}`)
      lines.push(`Pantalla: ${env.screen} · Viewport: ${env.viewport}`)
      lines.push(`Zona horaria: ${env.timezone} · Idioma: ${env.language}`)
    }
    lines.push('')
    lines.push('— Conectividad —')
    for (const c of checks) {
      const icon = c.status === 'ok' ? '[OK] ' : c.status === 'fail' ? '[FAIL]' : c.status === 'warn' ? '[WARN]' : '[?]  '
      const ms = c.ms != null ? `${c.ms}ms` : '-'
      lines.push(`${icon} ${c.label} — ${ms}`)
      lines.push(`        ${c.detail}`)
      if (c.error) lines.push(`        ${c.error}`)
    }
    return lines.join('\n')
  }

  const handleCopy = async () => {
    const text = buildReport()
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const update = (id: string, patch: Partial<Check>) => {
    setChecks(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c))
  }

  const runAll = async () => {
    setRunning(true)
    setFinishedAt(null)
    setEnv(null)
    setChecks(buildChecks(apiUrl, wsUrl))

    const httpTargets: Array<[string, string]> = [
      ['focus', window.location.origin + '/'],
      ...(MONITOR_BUNDLE_URL ? [['monitor-bundle', MONITOR_BUNDLE_URL] as [string, string]] : []),
      ...(CNF_BUNDLE_URL ? [['cnf-bundle', CNF_BUNDLE_URL] as [string, string]] : []),
      ['backend-https', apiUrl + (apiUrl.endsWith('/') ? '' : '/')],
      ['firebase-auth', 'https://identitytoolkit.googleapis.com/'],
      ['firestore', 'https://firestore.googleapis.com/'],
    ]

    const [_, wsR, clock, ipInfo, swCount] = await Promise.all([
      Promise.all(httpTargets.map(async ([id, url]) => {
        const r = await probeHttp(url)
        update(id, { status: r.ok ? 'ok' : 'fail', ms: r.ms, error: r.error })
      })),
      probeWebSocket(wsUrl + '/ws'),
      probeClockSkew(),
      probePublicIp(),
      probeServiceWorkers(),
    ])

    update('backend-wss', { status: wsR.ok ? 'ok' : 'fail', ms: wsR.ms, error: wsR.error })

    setEnv({
      publicIp: ipInfo.ip,
      ipLocation: ipInfo.location,
      clockSkewSec: clock.skewSec,
      clockStatus: clock.status,
      serviceWorkerCount: swCount,
      capabilities: probeCapabilities(),
      screen: `${window.screen.width}×${window.screen.height}`,
      viewport: `${window.innerWidth}×${window.innerHeight}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
    })

    setRunning(false)
    setFinishedAt(new Date())
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runAll() }, [])

  const anyFail = checks.some(c => c.status === 'fail')
  const allOk = !running && checks.every(c => c.status === 'ok')

  const summary = running
    ? 'Ejecutando diagnóstico...'
    : allOk
      ? 'Todo funciona correctamente'
      : anyFail
        ? 'Hay bloqueos de red. Comparte esta pantalla con el soporte de SUMMAFX.'
        : ''

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      color: '#fff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '32px 20px',
    }}>
      <div style={{ maxWidth: 780, margin: '0 auto' }}>
        <header style={{ marginBottom: 28 }}>
          <div style={{ color: '#f58c23', fontSize: 13, letterSpacing: 2, fontWeight: 700 }}>SUMMAFX · FOCUS</div>
          <h1 style={{ fontSize: 28, margin: '6px 0 4px' }}>Diagnóstico de conectividad</h1>
          <p style={{ color: '#888', margin: 0, fontSize: 14 }}>
            Esta página verifica si tu red puede conectarse a todos los servicios que Focus necesita.
            Si estás reportando un problema, toma una captura de pantalla y envíasela al soporte.
          </p>
        </header>

        <div style={{
          background: anyFail ? '#3a1010' : allOk ? '#0e2a14' : '#1a1a1a',
          border: `1px solid ${anyFail ? '#7a2020' : allOk ? '#1e5a2e' : '#333'}`,
          borderRadius: 10,
          padding: '14px 18px',
          marginBottom: 24,
          fontSize: 15,
          fontWeight: 500,
        }}>
          {summary || ' '}
        </div>

        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: '#666' }}>
            {finishedAt ? `Última ejecución: ${finishedAt.toLocaleString()}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={handleCopy}
              disabled={running}
              style={{
                background: copied ? '#1e5a2e' : '#242424',
                color: '#fff',
                border: '1px solid #3a3a3a',
                padding: '10px 18px',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 14,
                cursor: running ? 'default' : 'pointer',
                opacity: running ? 0.5 : 1,
                minWidth: 140,
              }}
            >
              {copied ? '✓ Copiado' : 'Copiar reporte'}
            </button>
            <button
              onClick={runAll}
              disabled={running}
              style={{
                background: '#f58c23',
                color: '#000',
                border: 0,
                padding: '10px 18px',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 14,
                cursor: running ? 'default' : 'pointer',
                opacity: running ? 0.5 : 1,
              }}
            >
              {running ? 'Ejecutando...' : 'Volver a ejecutar'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {checks.map(c => (
            <div key={c.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              background: '#141414',
              border: '1px solid #242424',
              borderRadius: 8,
              padding: '12px 16px',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: c.status === 'ok' ? '#1e5a2e' : c.status === 'fail' ? '#7a2020' : '#333',
                color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0,
              }}>
                {c.status === 'ok' ? '✓' : c.status === 'fail' ? '✕' : '…'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 500 }}>{c.label}</div>
                {c.status === 'fail' && c.error && (
                  <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                    No se pudo establecer la conexión
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#666', fontVariantNumeric: 'tabular-nums' }}>
                {c.ms != null ? `${c.ms} ms` : ''}
              </div>
            </div>
          ))}
        </div>

        {env && (
          <div style={{
            background: '#141414',
            border: '1px solid #242424',
            borderRadius: 10,
            padding: '14px 18px',
            marginTop: 24,
            fontSize: 13,
            color: '#bbb',
            lineHeight: 1.7,
          }}>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: 6 }}>Entorno</div>
            <div>IP pública: <span style={{ color: '#fff', fontFamily: 'monospace' }}>{env.publicIp ?? '—'}</span>{env.ipLocation ? ` · ${env.ipLocation}` : ''}</div>
            <div>
              Reloj del equipo:{' '}
              <span style={{ color: env.clockStatus === 'ok' ? '#4ade80' : env.clockStatus === 'warn' ? '#fbbf24' : '#f87171', fontFamily: 'monospace' }}>
                {env.clockSkewSec! >= 0 ? '+' : ''}{env.clockSkewSec}s
              </span>
              {env.clockStatus === 'fail' && ' · El reloj está desfasado, esto puede impedir el inicio de sesión'}
              {env.clockStatus === 'warn' && ' · Desfase leve'}
            </div>
            <div>
              Navegador:{' '}
              <span style={{ color: env.capabilities.cookies ? '#bbb' : '#f87171' }}>{env.capabilities.cookies ? 'cookies ok' : 'cookies DESHABILITADAS'}</span>
              {' · '}
              <span style={{ color: env.capabilities.localStorage ? '#bbb' : '#f87171' }}>{env.capabilities.localStorage ? 'storage ok' : 'storage DESHABILITADO'}</span>
              {' · '}
              <span style={{ color: env.capabilities.webSocket ? '#bbb' : '#f87171' }}>{env.capabilities.webSocket ? 'websocket ok' : 'websocket NO DISPONIBLE'}</span>
            </div>
            {env.serviceWorkerCount > 0 && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #242424' }}>
                <span style={{ color: '#fbbf24' }}>Hay {env.serviceWorkerCount} versión(es) en caché.</span>{' '}
                <button
                  onClick={clearCachesAndReload}
                  style={{ background: 'transparent', color: '#f58c23', border: '1px solid #f58c23', padding: '4px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer', marginLeft: 6 }}
                >
                  Limpiar y recargar
                </button>
              </div>
            )}
          </div>
        )}

        {anyFail && !running && (
          <div style={{
            marginTop: 24,
            background: '#141414',
            border: '1px solid #242424',
            borderRadius: 8,
            padding: 16,
            fontSize: 13,
            color: '#aaa',
            lineHeight: 1.6,
          }}>
            <div style={{ color: '#fff', fontWeight: 600, marginBottom: 6 }}>Hay servicios inaccesibles desde tu red.</div>
            Toca <strong style={{ color: '#fff' }}>Copiar reporte</strong> y envíalo al soporte de SUMMAFX por el canal habitual.
            Nuestro equipo identificará qué debe habilitar el área de TI de tu organización.
          </div>
        )}
      </div>
    </div>
  )
}
